import {areSetsEqual} from '@benchling/js-utils';
import {produce} from 'immer';
import {assign, setup, fromPromise, assertEvent} from 'xstate';

import {ANTIBODY_CONSTANT_DOMAIN_TYPES} from 'antibodies/AntibodyConstants';
import {distinctDomainToKey, formatToSortedDistinctDomainsWithChainIndex} from 'antibodies/AntibodyUtils';
import {areConstantDomainsFullyDefined} from 'antibodies/nested-data-creation/AntibodyNestedDataCreationUtil';
import {
  AntibodyNestedDataCreationState,
  ConstantAaDomainEntity,
  INITIAL_ANTIBODY_NESTED_DATA_CREATION_STATE,
  TranslationParameters,
} from 'antibodies/nested-data-creation/constants/AntibodyNestedDataCreationConstants';
import {createAntibodiesFromDnaPairs} from 'antibodies/nested-data-creation/NestedDataCreationDAO';
import {assert} from 'Asserts';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import notifications from 'notifications';
import * as LoadingScreen from 'ui-library/LoadingScreen';

import {AntibodyFormatGraphQL} from '../../../graphql-fragments/antibodies/__generated/AntibodyFormatGraphQL';

type AntibodyNestedDataCreationModalEvents =
  | {type: 'next'}
  | {type: 'back'}
  | {
      type: 'selectRegistryAndDependentConfig';
      registryId: string | null;
      pairSchemaId: string | null;
      constantDomainSchemaId: string | null;
    }
  | {type: 'selectFormat'; format: AntibodyFormatGraphQL | null}
  | {type: 'setProjectFolder'; folderId: string}
  | {type: 'setTranslationParameters'; translationParameters: TranslationParameters}
  | {type: 'selectConstantDomains'; constantDomainEntities: Record<string, ConstantAaDomainEntity>}
  | {type: 'selectDnaDomainPairs'; pairIds: Array<string>}
  | {type: 'createAndRegister'};

async function handleCreateAntibodies({
  pairIds,
  constantDomainEntities,
  folderId,
  registryId,
  formatId,
}: {
  pairIds: Array<string>;
  constantDomainEntities: Array<ConstantAaDomainEntity>;
  folderId: string | null;
  registryId: string | null;
  formatId: string | null;
}): Promise<void> {
  assert(
    areConstantDomainsFullyDefined(constantDomainEntities),
    'Expected an entity for every constant domain'
  );
  assert(folderId != null, 'Expected a folder ID');
  assert(registryId != null, 'Expected a registry ID');
  assert(formatId != null, 'Expected a format ID');
  try {
    LoadingScreen.show('Creating antibodies from DNA pairs...');
    const result = await createAntibodiesFromDnaPairs({
      dnaPairIds: pairIds,
      constantDomains: constantDomainEntities,
      folderId,
      registryId,
      formatId,
    });
    if (result.createdAllEntities) {
      notifications.success('Successfully created antibodies and components');
    } else {
      notifications.info("We're working on creating antibodies and components...");
    }
  } catch (e) {
    notifications.notifyError(e);
    throw e;
  } finally {
    LoadingScreen.hide();
  }
}

/**
 * State machine handling the progress and state of configuring antibody nested data creation in a modal.
 */
const AntibodyNestedDataCreationModalMachine = setup({
  types: {
    context: {} as AntibodyNestedDataCreationState,
    events: {} as AntibodyNestedDataCreationModalEvents,
    input: {} as {initialFolderId?: string | null},
  },
  actors: {
    submit: fromPromise(
      async ({
        input,
      }: {
        input: {
          pairIds: Array<string>;
          constantDomainEntities: Array<ConstantAaDomainEntity>;
          folderId: string | null;
          registryId: string | null;
          formatId: string | null;
        };
      }) => {
        await handleCreateAntibodies(input);
        return {success: true};
      }
    ),
  },
  actions: {
    selectRegistryAndDependentConfig: assign(({context, event}) => {
      assertEvent(event, 'selectRegistryAndDependentConfig');
      return produce(context, (draft) => {
        draft.selectedRegistryId = event.registryId;
        draft.inputConfig.variableDnaDomains.pairSchemaId = event.pairSchemaId;
        draft.inputConfig.constantAaDomains.schemaId = event.constantDomainSchemaId;
      });
    }),
    selectFormat: assign(({context, event}) => {
      assertEvent(event, 'selectFormat');
      const newFormatId = event.format?.id ?? null;
      if (context.selectedFormatId === newFormatId) {
        return context;
      }
      return produce(context, (draft) => {
        draft.selectedFormatId = newFormatId;
        if (event.format != null) {
          // When a format is selected, prepopulate the constant domain entities with all required constant domains for that format
          const constantDomains = Object.values(
            formatToSortedDistinctDomainsWithChainIndex(event.format)
          ).filter((domain) => ANTIBODY_CONSTANT_DOMAIN_TYPES.includes(domain.type));
          draft.inputConfig.constantAaDomains.entities = constantDomains.reduce(
            (acc, domain) => {
              acc[distinctDomainToKey(domain)] = {...domain, aaSequenceId: null};
              return acc;
            },
            {} as Record<string, ConstantAaDomainEntity>
          );
        } else {
          draft.inputConfig.constantAaDomains.entities = {};
        }
      });
    }),
    selectDnaDomainPairs: assign(({context, event}) => {
      assertEvent(event, 'selectDnaDomainPairs');
      return produce(context, (draft) => {
        draft.inputConfig.variableDnaDomains.pairIds = event.pairIds;
      });
    }),
    setTranslationParameters: assign(({context, event}) => {
      assertEvent(event, 'setTranslationParameters');
      return produce(context, (draft) => {
        draft.outputConfig.variableAaDomains.translationParameters = event.translationParameters;
      });
    }),
    selectConstantDomains: assign(({context, event}) => {
      assertEvent(event, 'selectConstantDomains');
      // Since selectFormat prepopulates all the constant domains on a format, this assertion ensures
      // that the required constant domains are not changed unexpectedly.
      assert(
        areSetsEqual(
          new Set(Object.keys(event.constantDomainEntities)),
          new Set(Object.keys(context.inputConfig.constantAaDomains.entities))
        ),
        'Number of constant domain entities should not change on selection'
      );
      return produce(context, (draft) => {
        draft.inputConfig.constantAaDomains.entities = event.constantDomainEntities;
      });
    }),
    setProjectFolder: assign(({context, event}) => {
      assertEvent(event, 'setProjectFolder');
      return produce(context, (draft) => {
        draft.outputConfig.antibodies.folderId = event.folderId;
      });
    }),
  },
}).createMachine({
  context: ({input}) =>
    produce(INITIAL_ANTIBODY_NESTED_DATA_CREATION_STATE, (draft) => {
      draft.outputConfig.antibodies.folderId =
        input.initialFolderId ?? FolderDefaults.get(FolderDefaultsKey.INVENTORY) ?? null;
    }),
  id: 'antibodyNestedDataCreationModal',
  initial: 'selectFormat',
  states: {
    selectFormat: {
      initial: 'defineCreation',
      on: {
        next: {
          target: 'defineDomains',
          guard: ({context}) =>
            context.selectedRegistryId != null &&
            context.selectedFormatId != null &&
            context.inputConfig.variableDnaDomains.pairSchemaId != null &&
            context.inputConfig.constantAaDomains.schemaId != null,
        },
      },
      states: {
        defineCreation: {
          on: {
            selectRegistryAndDependentConfig: {actions: 'selectRegistryAndDependentConfig'},
            selectFormat: {actions: 'selectFormat'},
          },
        },
      },
    },
    defineDomains: {
      initial: 'defineDomains',
      on: {
        next: {
          target: 'finalize',
          guard: ({context}) =>
            context.inputConfig.variableDnaDomains.pairIds.length > 0 &&
            areConstantDomainsFullyDefined(Object.values(context.inputConfig.constantAaDomains.entities)),
        },
        back: {
          target: 'selectFormat',
        },
      },
      states: {
        defineDomains: {
          on: {
            setTranslationParameters: {actions: 'setTranslationParameters'},
            selectDnaDomainPairs: {actions: 'selectDnaDomainPairs'},
            selectConstantDomains: {actions: 'selectConstantDomains'},
          },
        },
      },
    },
    finalize: {
      initial: 'finalizeStep',
      on: {
        back: {
          target: 'defineDomains',
        },
        createAndRegister: {
          target: 'saving',
        },
      },
      states: {
        finalizeStep: {
          on: {
            setProjectFolder: {actions: 'setProjectFolder'},
          },
        },
      },
    },
    saving: {
      invoke: {
        src: 'submit',
        input: ({context}) => ({
          pairIds: context.inputConfig.variableDnaDomains.pairIds,
          constantDomainEntities: Object.values(context.inputConfig.constantAaDomains.entities),
          folderId: context.outputConfig.antibodies.folderId,
          registryId: context.selectedRegistryId,
          formatId: context.selectedFormatId,
        }),
        onDone: {
          target: 'done',
        },
        onError: {
          target: 'finalize',
        },
      },
    },
    done: {
      type: 'final',
    },
  },
});

export default AntibodyNestedDataCreationModalMachine;
